import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  CheckBoxContainer,
  CheckBoxFinePrintLabel,
  SectionTitle,
} from "../../../../../../components/Form/Form";
import { Form } from "../../../../../../layout/FormLayout";
import { Controller } from "react-hook-form";
import { SelectBoxV2 } from "../../../../../../components/SelectBoxV2/SelectBoxV2";
import {
  ButtonContainer,
  ButtonWithWarningDialog,
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import {
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../../../util/zod.util";
import { z } from "zod";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import {
  toTitleCase,
  useFormWrapper,
  useStoreState,
} from "../../../../../../util/util";
import { FilePickerUncontrolled } from "../../../../../../components/FilePicker/FilePickerUncontrolled";
import type {
  Language,
  OptionType,
  SupportedLanguage,
} from "../../../../../../types/types";
import type {
  SupportedAssetCategoryType,
  SupportedAssetType,
  UploadAssetsResponseSchema,
  UploadedAssetInProgressSchema,
  ProductUploadStatus,
  ListDetailsSchema,
} from "../../../../../../types/types.PIM";
import { zodResolver } from "@hookform/resolvers/zod";
import { strings } from "../../../../../../util/strings";
import type { SupportedFileType } from "../../../../../../components/FilePicker/FilePicker.constants";
import {
  MAXIMUM_FILE_SIZE,
  SUPPORTED_DOC_OR_ZIP_TYPES,
  SUPPORTED_IMG_OR_ZIP_TYPES,
  SUPPORTED_VIDEO_OR_ZIP_TYPES,
} from "../../../../../../components/FilePicker/FilePicker.constants";
import useSWR from "swr";
import type { AssetCategory, AssetType } from "../util/AssetsUtil";
import { endpoints } from "../../../../../../endpoints";
import { DelayedSpinner } from "../../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../../components/Error";
import { HelpDialog } from "../../../../../SharedPages/OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/HelpDialog";
import {
  H6Bold,
  SmallSectionHeaderRegular,
} from "../../../../../../components/Typography/Typography";
import { InfoIcon } from "../../../../../../components/Icons/Icons";
import { UploadPopups } from "../../../../../SharedPages/OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/UploadPopups";
import type { AxiosError } from "axios";
import axios from "axios";
import { ProcessUploadedAssets } from "./ProcessUploadedAssets";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { CheckBoxNoLabel } from "../../../../../../components/CheckBoxes/CheckBoxes";
import { spacingTokens } from "../../../../../../designTokens";

const AddFilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const GuideSection = styled.section`
  position: absolute;
  right: 0;
  top: 89px;
`;

const GuideContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddAssetSchema = z.object({
  asset_name: z.string(),
  files: z.instanceof(File).array(),
  asset_type: zodSelectBoxType,
  category_id: zodSelectBoxType,
  asset_host_id: zodSelectBoxType,
  language: zodSelectBoxType,
  replace_existing: z.boolean().default(false).optional(),
});

const guidelineOverview = (t: TFunction) => [
  t(
    "Select an Asset Type, Category, and Language to assign to your uploaded assets."
  ),
  {
    text: t(
      "Use the upload box to select the asset files you would like to upload from your local file browser."
    ),
    subList: [
      t(
        "Checking the replace existing assets box will replace the existing assets with same file name."
      ),
      t(
        "Once your files have been uploaded, you will see a list of uploaded file names, along with the asset name, asset type, category, language, and size of each uploaded file."
      ),
      t(
        "If the replace existing assets is checked, you will see a list of files that already exists in the system."
      ),
      t(
        "We will automatically generate an asset name based on your uploaded file, but this can be edited within the list of uploaded files."
      ),
    ],
  },
  t("Please check for any errors that may be flagged on your uploaded files."),
  t(
    "Once you are satisfied with your file upload, click the Save button to save them to the system. Clicking Save will reset the upload page and will allow you to upload a new set of files with a different asset type & category."
  ),
];

const csvGuidelines = (t: TFunction) => [
  t("Each uploaded file must be less than 50 mb in size."),
  t("All files must have a unique file name."),
  t("You can only upload a document, image, or video file."),
];

type FormValues = z.infer<typeof AddAssetSchema>;

const AddAssetSchemaFn = (t: (s: string) => string) =>
  z
    .object({
      asset_type: zodSelectBoxDefault(t),
      files: z.instanceof(File).array().min(1, strings(t).thisIsARequiredField),
      category_id: zodSelectBoxDefault(t),
      asset_host_id: zodSelectBoxDefault(t),
      language: zodSelectBoxDefault(t),
      replace_existing: z.boolean().default(false).optional(),
    })
    .superRefine(({ files }, ctx) => {
      const filesAboveMaxSize: File[] = [];
      const uniqueFileNameHash: { [prop: string]: string } = {};
      for (const file of files) {
        if (uniqueFileNameHash[file.name]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("All files must have unique a unique file name."),
            path: ["files"],
          });
          break;
        } else {
          uniqueFileNameHash[file.name] = file.name;
        }
      }
      files.forEach((file) => {
        if (file.size > MAXIMUM_FILE_SIZE) {
          filesAboveMaxSize.push(file);
        }
      });
      const len = filesAboveMaxSize.length;
      if (len > 0) {
        const fileNames = filesAboveMaxSize.reduce(
          (acc, curr, idx) => (idx === 0 ? `${acc}` : `${acc}, ${curr.name}`),
          `${filesAboveMaxSize[0].name}`
        );
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${fileNames} ${
            len > 1
              ? t("are above the maximum file size")
              : t("is above the maximum file size")
          }`,
          path: ["files"],
        });
      }
    });

export const AddAssetFiles = ({
  onCompleteUpload,
}: {
  onCompleteUpload: () => void;
}) => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess, notifyWarning } = useNotifications();
  const {
    tenant_id,
    storefront_id,
    storefront_metadata: { enable_external_assets },
  } = useStoreState();

  const [showDialog, setShowDialog] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [query, setQuery] = useQueryParams({
    uploadUUID: StringParam,
    uploadNumber: StringParam,
    count: NumberParam,
  });
  const [uploadUUID, setUploadUUID] = useState<string | undefined>(
    query?.uploadUUID ?? undefined
  );
  const [uploadNumber, setUploadNumber] = useState<string | undefined>(
    query?.uploadNumber ?? undefined
  );

  const [uploadedAssets, setUploadedAssets] = useState<
    UploadedAssetInProgressSchema[]
  >([]);
  const [existingUploadedAssets, setExistingUploadedAssets] = useState<
    UploadedAssetInProgressSchema[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<ProductUploadStatus>("in_progress");
  const [assetTypes, setAssetTypes] = useState<
    OptionType<SupportedAssetType>[]
  >([]);
  const [assetCategories, setAssetCategories] = useState<
    OptionType<SupportedAssetCategoryType>[]
  >([]);
  const [assetHosts, setAssetHosts] = useState<OptionType<string>[]>([]);
  const [assetLanguages, setAssetLanguages] = useState<
    OptionType<SupportedLanguage>[]
  >([]);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddAssetSchemaFn(t)),
    defaultValues: {
      asset_type: {
        label: undefined,
        value: undefined,
      },
      category_id: { label: undefined, value: undefined },
      files: [],
      language: { label: undefined, value: undefined },
    },
  });

  const {
    handleSubmit,
    control,
    errors,
    formState,
    register,
    setValue,
    watch,
  } = methodsOfUseForm;

  const assetType = watch("asset_type");

  const supportedFileTypes: SupportedFileType =
    assetType.value === "image"
      ? (SUPPORTED_IMG_OR_ZIP_TYPES as SupportedFileType)
      : assetType.value === "video"
      ? (SUPPORTED_VIDEO_OR_ZIP_TYPES as SupportedFileType)
      : (SUPPORTED_DOC_OR_ZIP_TYPES as SupportedFileType);

  const { data: assetTypeResponse, error: assetTypeError } = useSWR<AssetType>(
    endpoints.v2_storefronts_id_pim_asset_types()
  );

  const { data: uploadedAssetsResponse, mutate: mutateAssets } =
    useSWR<UploadAssetsResponseSchema>(
      query.uploadUUID
        ? endpoints.v2_tenants_tenant_id_pim_assets_uploads_upload_id(
            tenant_id,
            query.uploadUUID
          )
        : null
    );

  const { data: assetCategoriesResponse, error: categoryTypeError } =
    useSWR<AssetCategory>(
      assetType.value
        ? `${endpoints.v2_tenants_id_pim_assets_categories(
            tenant_id
          )}?asset_type=${assetType.value}`
        : null
    );

  const { data: assetsHostData, error: assetsHostError } = useSWR<{
    data: ListDetailsSchema;
  }>(endpoints.v2_tenants_id_pim_get_list_by_name(tenant_id, "Asset Host"), {
    onError: (error) => {
      console.error("There was an error fetching the Asset Host list.", {
        error,
      });
    },
  });

  const { data: assetLanguagesResponse, error: assetLanguagesError } = useSWR<{
    languages: Language[];
  }>(endpoints.v1_storefronts_id_languages(storefront_id));

  const assetHostDefaultValue = useMemo(() => {
    return (
      assetHosts.find((host) => host.label === "Agilis") ?? {
        label: undefined,
        value: undefined,
      }
    );
  }, [assetHosts]);

  const set_new_and_existing_assets = (
    data: UploadedAssetInProgressSchema[]
  ) => {
    const new_data: UploadedAssetInProgressSchema[] = [];
    const existing_data: UploadedAssetInProgressSchema[] = [];
    data.forEach((asset) => {
      if (asset.replace_asset_id) {
        existing_data.push(asset);
      } else {
        new_data.push(asset);
      }
    });
    setExistingUploadedAssets(existing_data);
    setUploadedAssets(new_data);
  };

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleUploadComplete = useCallback(
    (data: UploadAssetsResponseSchema) => {
      stopPolling();
      setShowDialog(false);
      const hasError = data.data.filter((asset) => asset.errors)?.length;
      if (hasError) {
        notifyWarning(
          t(
            `Your files have been uploaded with ${hasError ?? ""} error${
              hasError > 1 ? "s" : ""
            }, Please review the list of uploaded files for any flagged errors and generated Asset Names.`
          )
        );
      } else {
        notifySuccess(t("Your files have been uploaded successfully."));
      }
      set_new_and_existing_assets(data.data);
      requestAnimationFrame(() => {
        const parent = document.getElementById(
          "bulkupload_container"
        )?.parentElement;
        if (parent) {
          parent.scrollTo({ top: parent.scrollHeight, behavior: "smooth" });
        }
      });
    },
    [notifySuccess, notifyWarning, stopPolling, t]
  );

  const startPolling = useCallback(() => {
    stopPolling();
    intervalRef.current = setInterval(async () => {
      const data = await mutateAssets();
      if (data && data.number_uploaded === data.number_of_prospected_assets) {
        handleUploadComplete(data);
      }
    }, 1500);
  }, [handleUploadComplete, mutateAssets, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const onSubmit = async (values: FormValues) => {
    setStatus("in_progress");
    setDialogMessage(
      t("Your files are currently being uploaded in the background.")
    );
    setShowDialog(true);
    setIsSubmitting(true);
    try {
      const {
        asset_type,
        category_id,
        language,
        files,
        replace_existing,
        asset_host_id,
      } = values;
      const formData = new FormData();
      files.forEach((file) => formData.append(file.name, file));

      formData.append("asset_type", asset_type.value);
      formData.append("category_id", category_id.value);
      formData.append("asset_host_id", asset_host_id.value);
      formData.append("language", language.value);
      if (replace_existing !== undefined) {
        formData.append("replace_existing", String(replace_existing));
      }
      if (!uploadUUID) {
        const {
          data: { data: responseData, id: responseId, number, number_uploaded },
        } = await axios.post<UploadAssetsResponseSchema>(
          endpoints.v2_tenants_tenant_id_pim_assets_uploads(tenant_id),
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        setUploadUUID(responseId);
        setUploadNumber(number);
        setQuery({
          uploadUUID: responseId,
          uploadNumber: number,
          count: number_uploaded,
        });
        set_new_and_existing_assets(responseData);
      } else {
        const {
          data: { data: responseData, number_uploaded },
        } = await axios.patch<UploadAssetsResponseSchema>(
          endpoints.v2_tenants_tenant_id_pim_assets_uploads_upload_id(
            tenant_id,
            uploadUUID
          ),
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        setQuery({ count: number_uploaded });
        set_new_and_existing_assets(responseData);
      }
      setValue("files", []);
      startPolling();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error uploading your files."),
        {
          error,
        }
      );
      setShowDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (assetsHostData) {
      setAssetHosts(
        assetsHostData.data.items.map((asset_host) => ({
          label: asset_host.name,
          value: asset_host.id,
        }))
      );
    }
  }, [assetsHostData]);

  useEffect(() => {
    if (assetTypeResponse) {
      const { asset_types } = assetTypeResponse;
      setAssetTypes(
        asset_types.map((asset_type) => ({
          label: toTitleCase(asset_type),
          value: asset_type,
        }))
      );
    }
  }, [assetTypeResponse]);

  useEffect(() => {
    if (assetCategoriesResponse) {
      setAssetCategories(
        assetCategoriesResponse.asset_categories.map((category) => ({
          label: category.name,
          value: category.id,
        }))
      );
    }
  }, [assetCategoriesResponse]);

  useEffect(() => {
    if (assetLanguagesResponse) {
      setAssetLanguages(
        assetLanguagesResponse.languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
    }
  }, [assetLanguagesResponse]);

  useEffect(() => {
    if (
      uploadedAssetsResponse &&
      uploadedAssets.length === 0 &&
      existingUploadedAssets.length === 0
    ) {
      const { data, asset_type, category, language, replace_existing } =
        uploadedAssetsResponse;
      setValue(
        "asset_type",
        asset_type
          ? {
              label: toTitleCase(asset_type),
              value: asset_type,
            }
          : { label: undefined, value: undefined }
      );
      setValue(
        "category_id",
        category
          ? { label: category.name, value: category.id }
          : { label: undefined, value: undefined }
      );
      setValue(
        "language",
        language
          ? assetLanguages.find((lang) => lang.value === language)
          : { label: undefined, value: undefined }
      );
      setValue("replace_existing", replace_existing ?? false);
      setReplaceExisting(replace_existing ?? false);
      set_new_and_existing_assets(data);
    }
  }, [
    assetLanguages,
    existingUploadedAssets.length,
    setValue,
    uploadedAssets.length,
    uploadedAssetsResponse,
  ]);

  useEffect(() => {
    register("replace_existing");
  }, [register]);

  const isLoadingAssetTypes = !assetTypeError && !assetTypeResponse;

  if (isLoadingAssetTypes) {
    return <DelayedSpinner />;
  }

  if (
    assetTypeError ||
    categoryTypeError ||
    assetLanguagesError ||
    assetsHostError
  ) {
    return (
      <ErrorPlaceholder
        message={t(
          "There was an error loading asset types and categories. Please try again later"
        )}
      />
    );
  }

  return (
    <AddFilesContainer>
      <SectionTitle style={{ margin: `${spacingTokens.spacing_xl} 0` }}>
        {t("Add files below")}
      </SectionTitle>
      <Form style={{ maxWidth: "512px" }} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="asset_type"
          autoComplete={"asset_type"}
          placeholder={t("Asset Type")}
          rules={{
            required: true,
          }}
          onInputChange={() => {
            setValue("category_id", { label: undefined, value: undefined });
            setValue("files", []);
          }}
          errors={errors}
          formState={formState}
          defaultValue={{ label: undefined, value: undefined }}
          isDisabled={uploadUUID}
          options={assetTypes}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="category_id"
          autoComplete={"category_id"}
          placeholder={t("Category")}
          options={assetCategories}
          rules={{
            required: true,
          }}
          errors={errors}
          isDisabled={!assetType.value || uploadUUID}
          formState={formState}
        />
        {!assetsHostError && !!assetHostDefaultValue.value && (
          <>
            {enable_external_assets ? (
              <Controller
                as={SelectBoxV2}
                control={control}
                name="asset_host_id"
                autoComplete={"asset_host_id"}
                placeholder={t("Asset Host")}
                defaultValue={assetHostDefaultValue}
                options={assetHosts}
                rules={{
                  required: true,
                }}
                errors={errors}
                isDisabled={true}
                formState={formState}
              />
            ) : (
              <Controller
                name="asset_host_id"
                control={control}
                defaultValue={assetHostDefaultValue}
                render={() => <input type="hidden" />}
              />
            )}
          </>
        )}
        <Controller
          as={SelectBoxV2}
          control={control}
          name="language"
          autoComplete={"language"}
          placeholder={t("Language")}
          options={assetLanguages}
          rules={{
            required: true,
          }}
          errors={errors}
          isDisabled={uploadUUID}
          formState={formState}
        />
        <FilePickerUncontrolled
          methodsOfUseForm={methodsOfUseForm}
          placeHolderText={t("Drag and drop files, OR")}
          multiple={true}
          name="files"
          accept={supportedFileTypes}
          description={t("Max file size: 50 mb / file")}
          required={false}
        />
        <CheckBoxContainer>
          <div style={{ minWidth: "12px" }}>
            <CheckBoxNoLabel
              id="replace_existing"
              style={{ marginRight: "5px" }}
              onChange={(e) => {
                setReplaceExisting(e.target.checked);
                setValue("replace_existing", e.target.checked);
              }}
              disabled={!!uploadUUID}
              checked={replaceExisting}
            />
          </div>
          <CheckBoxFinePrintLabel
            style={{ cursor: !!uploadUUID ? "not-allowed" : "pointer" }}
            htmlFor="replace_existing"
          >
            {t("Replace existing files")}
          </CheckBoxFinePrintLabel>
        </CheckBoxContainer>
        <ButtonContainer>
          {!!uploadUUID ? (
            <ButtonWithWarningDialog
              Button={(props) => (
                <TertiaryButtonMediumV2 {...props}>
                  {t("Back")}
                </TertiaryButtonMediumV2>
              )}
              testid={`close_upload_${uploadNumber}`}
              confirmMessage={t(
                "Your files have not been saved yet. Leaving this page will cancel your assets upload. Are you sure you want to continue?"
              )}
              handleConfirm={onCompleteUpload}
              heading={t("Cancel Upload?")}
              id={uploadNumber}
            />
          ) : (
            <TertiaryButtonMediumV2 type="button" onClick={onCompleteUpload}>
              {t("Back")}
            </TertiaryButtonMediumV2>
          )}
          <PrimaryButtonMediumV2 type="submit" loading={isSubmitting}>
            {t("Upload")}
          </PrimaryButtonMediumV2>
        </ButtonContainer>
      </Form>
      <GuideSection>
        <GuideContainer
          style={{ alignSelf: "flex-start", gap: spacingTokens.spacing_xs }}
        >
          <HelpDialog
            show={showGuide}
            setShow={setShowGuide}
            overview={guidelineOverview}
            guidelines={csvGuidelines}
            guidelineHeader={t("Guidelines for Updating Your Files")}
          />
          <SmallSectionHeaderRegular style={{ margin: 0 }}>
            {t("Need help getting started?")}
          </SmallSectionHeaderRegular>
          <div
            onClick={() => setShowGuide(true)}
            style={{
              display: "flex",
              gap: spacingTokens.spacing_xs,
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <InfoIcon />
            <H6Bold>{t("View Guide")}</H6Bold>
          </div>
        </GuideContainer>
      </GuideSection>
      {uploadUUID ? (
        <ProcessUploadedAssets
          uploadUUID={uploadUUID}
          uploadNumber={uploadNumber ?? ""}
          assets={uploadedAssets}
          existingAssets={existingUploadedAssets}
          onComplete={() => {
            setUploadUUID(undefined);
            setUploadNumber(undefined);
            onCompleteUpload();
          }}
        />
      ) : (
        <></>
      )}
      <UploadPopups
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        onComplete={() => setShowDialog(false)}
        message={dialogMessage}
        status={status}
        uploadId={uploadNumber ?? ""}
        hideCloseBtn={status === "in_progress" ? false : true}
      />
    </AddFilesContainer>
  );
};
